import { useEffect, useRef, useState } from "react";
import type {
  CampaignOperationKind,
  CampaignOperationLocale,
  CampaignOperationPreviewInput,
} from "@atsready/shared";
import {
  MIN_TRIAL_DURATION_DAYS,
  MAX_TRIAL_DURATION_DAYS,
  TRIAL_DURATION_DAYS,
} from "@atsready/shared";
import type { AdminCampaign } from "@/services/types";
import { useAuth } from "@/contexts/AuthContext";
import { useCampaignEmailOperation } from "@/hooks/useCampaignEmailOperation";
import {
  campaignEmailButtonClass,
  campaignEmailCopy,
  campaignEmailLocale,
  campaignEmailReason,
} from "@/lib/campaign-email-copy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { CampaignEmailRecipients } from "./CampaignEmailRecipients";
import { CampaignEmailProgress } from "./CampaignEmailProgress";

export function CampaignEmailPanel({ campaign }: { campaign: AdminCampaign | null }) {
  const { user } = useAuth();
  const locale = campaignEmailLocale();
  const copy = campaignEmailCopy[locale];
  if (user?.role !== "ADMIN" && user?.role !== "SUPER_ADMIN")
    return (
      <section lang={locale} className="rounded-2xl border p-4">
        <h2 className="text-lg font-semibold">{copy.title}</h2>
        <p>{copy.permission}</p>
      </section>
    );
  return <AuthorizedPanel campaign={campaign} locale={locale} />;
}

function AuthorizedPanel({
  campaign,
  locale,
}: {
  campaign: AdminCampaign | null;
  locale: CampaignOperationLocale;
}) {
  const copy = campaignEmailCopy[locale];
  const operation = useCampaignEmailOperation();
  const [kind, setKind] = useState<CampaignOperationKind>("announce_grant");
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState(String(TRIAL_DURATION_DAYS));
  const [contentLocale, setContentLocale] = useState<CampaignOperationLocale>("en");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [expiredReviewId, setExpiredReviewId] = useState<string | null>(null);
  const [selectionCampaign, setSelectionCampaign] = useState(campaign);
  const cancel = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const { invalidatePreview } = operation;
  if (
    selectionCampaign?.id !== campaign?.id ||
    selectionCampaign?.updatedAt !== campaign?.updatedAt
  ) {
    setSelectionCampaign(campaign);
    setOpen(false);
    setSelectedIds([]);
  }
  useEffect(() => {
    invalidatePreview();
  }, [campaign?.id, campaign?.updatedAt, invalidatePreview]);
  useEffect(() => {
    const review = operation.preview;
    if (!review) return;
    const remaining = Date.parse(review.operation.reviewExpiresAt) - Date.now();
    const timer = setTimeout(
      () => setExpiredReviewId(review.operation.id),
      Math.min(2_147_483_647, Math.max(0, remaining)),
    );
    return () => clearTimeout(timer);
  }, [operation.preview]);
  const expired = operation.preview !== null && expiredReviewId === operation.preview.operation.id;
  const changed = () => {
    invalidatePreview();
    setOpen(false);
  };
  const durationValid =
    Number.isInteger(Number(duration)) &&
    Number(duration) >= MIN_TRIAL_DURATION_DAYS &&
    Number(duration) <= MAX_TRIAL_DURATION_DAYS;
  const valid =
    reason.trim().length > 0 &&
    reason.trim().length <= 500 &&
    selectedIds.length > 0 &&
    selectedIds.length <= 100 &&
    (kind !== "grant_pro_plus" || durationValid);
  const blocked = operation.busy || operation.previewLoading || operation.blockedUntil > 0;
  const prepare = (savedCampaign: AdminCampaign) => {
    const common = { userIds: selectedIds, reason: reason.trim(), contentLocale };
    const input: CampaignOperationPreviewInput =
      kind === "grant_pro_plus"
        ? { ...common, kind, durationDays: Number(duration) }
        : { ...common, kind };
    void operation.prepare(savedCampaign.id, input);
  };
  const preview = operation.preview;
  return (
    <section
      lang={locale}
      className="min-w-0 space-y-6 rounded-2xl border bg-card p-4 sm:p-6"
      aria-busy={operation.busy}
    >
      <h2 ref={heading} tabIndex={-1} className="text-lg font-semibold">
        {copy.title}
      </h2>
      {operation.error && (
        <p role="alert" className="rounded-xl border p-3">
          {copy[operation.error]}
          {operation.blockedUntil > 0
            ? ` ${copy.blockedUntil}: ${new Date(operation.blockedUntil).toLocaleString(locale)}`
            : ""}
        </p>
      )}
      {!campaign ? (
        <p>{copy.prerequisite}</p>
      ) : (
        <div className="grid min-w-0 gap-6 xl:grid-cols-2">
          <div className="min-w-0 space-y-4">
            <p className="break-words">
              {copy.savedCampaign}: {campaign.name}
            </p>
            <label className="block space-y-1">
              <span>{copy.action}</span>
              <select
                className="min-h-11 w-full min-w-0 rounded-xl border bg-background p-2"
                value={kind}
                disabled={blocked}
                onChange={(event) => {
                  changed();
                  setKind(event.target.value as CampaignOperationKind);
                }}
              >
                {(["announce_grant", "marketing_offer", "grant_pro_plus"] as const).map((value) => (
                  <option key={value} value={value}>
                    {copy[value]}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-sm text-muted-foreground">
              {kind === "announce_grant"
                ? copy.announceHelp
                : kind === "marketing_offer"
                  ? copy.offerHelp
                  : copy.grantHelp}
            </p>
            {kind === "grant_pro_plus" && (
              <label className="block space-y-1">
                <span>{copy.duration}</span>
                <Input
                  className="min-h-11"
                  type="number"
                  required
                  min={MIN_TRIAL_DURATION_DAYS}
                  max={MAX_TRIAL_DURATION_DAYS}
                  step={1}
                  value={duration}
                  disabled={blocked}
                  aria-label={copy.duration}
                  aria-invalid={!durationValid}
                  aria-describedby="campaign-duration-help"
                  onChange={(event) => {
                    changed();
                    setDuration(event.target.value);
                  }}
                />
                <span id="campaign-duration-help" className="block text-sm">
                  {copy.durationHelp}
                </span>
              </label>
            )}
            <label className="block space-y-1">
              <span>{copy.locale}</span>
              <select
                className="min-h-11 w-full rounded-xl border bg-background p-2"
                value={contentLocale}
                disabled={blocked}
                onChange={(event) => {
                  changed();
                  setContentLocale(event.target.value as CampaignOperationLocale);
                }}
              >
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span>{copy.reason}</span>
              <Textarea
                aria-label={copy.reason}
                required
                maxLength={500}
                value={reason}
                disabled={blocked}
                aria-describedby="campaign-reason-help"
                onChange={(event) => {
                  changed();
                  setReason(event.target.value);
                }}
              />
              <span id="campaign-reason-help" className="block text-sm">
                {copy.reasonHelp}
              </span>
            </label>
            <CampaignEmailRecipients
              selectedIds={selectedIds}
              locale={locale}
              disabled={blocked}
              onChange={(ids) => {
                changed();
                setSelectedIds(ids);
              }}
            />
            <Button
              type="button"
              className={campaignEmailButtonClass}
              disabled={blocked || !valid}
              onClick={() => prepare(campaign)}
            >
              {copy.preview}
            </Button>
          </div>
          <div className="min-w-0 space-y-4">
            {preview && (
              <>
                <h3 className="font-semibold">{copy.review}</h3>
                <p>
                  {copy.selected}: {preview.operation.counts.total} · {copy.eligible}:{" "}
                  {preview.operation.counts.eligible} · {copy.excluded}:{" "}
                  {preview.operation.counts.excluded}
                </p>
                <p>
                  {copy.expires}: {preview.operation.reviewExpiresAt}
                </p>
                {preview.operation.kind === "grant_pro_plus" && <p>{copy.estimated}</p>}
                {preview.operation.counts.eligible === 0 && <p>{copy.noEligible}</p>}
                <ul className="space-y-2">
                  {operation.previewRecipients?.map((recipient) => (
                    <li key={recipient.id} className="break-words">
                      {recipient.email} · {recipient.recipientLocale.toUpperCase()} ·{" "}
                      {recipient.eligibilityReason
                        ? campaignEmailReason(recipient.eligibilityReason, locale)
                        : copy.eligible}
                      {recipient.grantEndAt ? ` · ${copy.end}: ${recipient.grantEndAt}` : ""}
                    </li>
                  ))}
                </ul>
                {operation.previewLoading && <p role="status">{copy.loading}</p>}
                {(preview.nextCursor || operation.previewCursor) && (
                  <div className="flex flex-wrap gap-2" role="group" aria-label={copy.previewPages}>
                    <Button
                      className={campaignEmailButtonClass}
                      variant="outline"
                      disabled={blocked || !operation.previewCursor}
                      onClick={operation.previousPreviewRecipients}
                    >
                      {copy.previousPreview}
                    </Button>
                    <Button
                      className={campaignEmailButtonClass}
                      variant="outline"
                      disabled={blocked || !operation.previewNextCursor}
                      onClick={operation.nextPreviewRecipients}
                    >
                      {copy.nextPreview}
                    </Button>
                    {operation.previewPageError && (
                      <Button
                        className={campaignEmailButtonClass}
                        variant="outline"
                        disabled={blocked}
                        onClick={() => {
                          void operation.reloadPreviewRecipients();
                        }}
                      >
                        {copy.reloadPreview}
                      </Button>
                    )}
                  </div>
                )}
                {preview.samples.map((sample) => (
                  <div key={sample.locale} className="min-w-0 space-y-2">
                    <p className="break-words">
                      {copy.subject}: {sample.subject} · {sample.locale.toUpperCase()}
                    </p>
                    <iframe
                      title={`${copy.sample} · ${sample.locale.toUpperCase()}`}
                      sandbox=""
                      referrerPolicy="no-referrer"
                      srcDoc={sample.html}
                      className="h-[420px] w-full min-w-0 rounded-xl border bg-white"
                    />
                  </div>
                ))}
                {expired && <p>{copy.expired}</p>}
                <Button
                  ref={opener}
                  type="button"
                  className={campaignEmailButtonClass}
                  disabled={blocked || expired || preview.operation.counts.eligible === 0}
                  onClick={() => setOpen(true)}
                >
                  {copy.review}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      <section className="min-w-0 space-y-3">
        <h3 className="font-semibold">{copy.history}</h3>
        {operation.history.isFetching && <p role="status">{copy.loading}</p>}
        {operation.history.data?.operations.length === 0 && <p>{copy.emptyHistory}</p>}
        <ul className="space-y-2">
          {operation.history.data?.operations.map((item) => (
            <li
              key={item.id}
              className="flex min-w-0 flex-wrap items-center gap-3 rounded-xl border p-3"
            >
              <span className="min-w-0 break-words">
                {item.campaignName} · {copy[item.kind]} · {item.createdAt} · {copy.pending}:{" "}
                {item.counts.pending} · {copy.accepted}: {item.counts.accepted}
              </span>
              <Button
                className={campaignEmailButtonClass}
                variant="outline"
                disabled={blocked}
                onClick={() => operation.selectOperation(item.id)}
              >
                {copy.detail}
              </Button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          <Button
            className={campaignEmailButtonClass}
            variant="outline"
            disabled={blocked || operation.history.isFetching}
            onClick={() => {
              void operation.reloadHistory();
            }}
          >
            {copy.reload}
          </Button>
          {operation.historyCursor && (
            <Button
              className={campaignEmailButtonClass}
              variant="outline"
              disabled={blocked}
              onClick={operation.previousHistory}
            >
              {copy.previous}
            </Button>
          )}
          {operation.history.data?.nextCursor && (
            <Button
              className={campaignEmailButtonClass}
              variant="outline"
              disabled={blocked}
              onClick={operation.nextHistory}
            >
              {copy.next}
            </Button>
          )}
        </div>
      </section>
      {operation.statusLoading && <p role="status">{copy.loading}</p>}
      {operation.detail && (
        <CampaignEmailProgress
          detail={operation.detail}
          locale={locale}
          busy={operation.busy}
          running={operation.running}
          blockedUntil={operation.blockedUntil}
          onResume={() => {
            void operation.resume();
          }}
          onPause={operation.pause}
          onRetry={() => {
            void operation.retry();
          }}
          onReconcile={(id) => {
            void operation.reconcile(id);
          }}
          onReload={() => {
            void operation.reload();
          }}
          onNext={operation.nextRecipients}
          onPrevious={operation.previousRecipients}
          hasPrevious={!!operation.recipientCursor}
        />
      )}
      <Dialog open={open && !!preview} onOpenChange={setOpen}>
        <DialogContent
          lang={locale}
          hideClose
          className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg min-w-0 overflow-y-auto"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cancel.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (opener.current?.isConnected && !opener.current.disabled) opener.current.focus();
            else heading.current?.focus();
          }}
        >
          <DialogTitle>{copy.review}</DialogTitle>
          <DialogDescription>
            {preview ? copy[preview.operation.kind] : copy.title}
          </DialogDescription>
          {preview && (
            <>
              <p>
                {copy.eligible}: {preview.operation.counts.eligible} · {copy.excluded}:{" "}
                {preview.operation.counts.excluded}
              </p>
              <p className="break-words">
                {copy.reason}: {preview.operation.reason}
              </p>
              {preview.operation.durationDays !== null && (
                <p>
                  {copy.duration}: {preview.operation.durationDays}
                </p>
              )}
              <p>
                {preview.operation.kind === "grant_pro_plus"
                  ? copy.grantHelp
                  : preview.operation.kind === "marketing_offer"
                    ? copy.offerHelp
                    : copy.announceHelp}
              </p>
              {expired && <p>{copy.expired}</p>}
            </>
          )}
          <div className="flex flex-wrap gap-2">
            <DialogClose asChild>
              <Button ref={cancel} className={campaignEmailButtonClass} variant="outline">
                {copy.cancel}
              </Button>
            </DialogClose>
            <Button
              className={campaignEmailButtonClass}
              disabled={blocked || expired}
              onClick={() => {
                setOpen(false);
                void operation.confirm();
              }}
            >
              {kind === "grant_pro_plus" ? copy.grant_pro_plus : copy.confirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
